import React, { useEffect, useState, useMemo, useRef } from "react";
import { v4 } from "uuid";
import { useTranslation } from "react-i18next";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Partner, PartnerStatus, PartnerType } from "../../models/Partner";
import { RoleType } from "../../models/Role";
import { NewPartnerForm } from "../../forms/NewPartnerForm/NewPartnerForm";
import { getAllPartners, getOnePartner } from "../../services/PartnerService";
import { activeUser } from "../../services/AuthService";
import "./PartnersPage.scss";

export const PartnersPage = () => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);

  const [processing, setProcessing] = useState(false);
  const [allPartners, setAllPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Partner | undefined>();
  const [filteredPartners, setFilteredPartners] = useState<Partner[]>([]);
  const [showForm, setShowForm] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    code: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    address: { value: null, matchMode: FilterMatchMode.CONTAINS },
    cityName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    mobile: { value: null, matchMode: FilterMatchMode.CONTAINS },
    email: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    isVat: { value: null, matchMode: FilterMatchMode.EQUALS },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const refreshPartners = async () => {
    setProcessing(true);
    try {
      const resp = await getAllPartners();
      const list: Partner[] = resp?.data?.result ?? resp?.data ?? [];
      const normalized = list.map((p: any) => ({
        ...p,
        cityName: p?.city?.name ?? "",
      }));
      setAllPartners(normalized);
    } catch (e) {
      // @ts-ignore
      toast.current?.show({
        severity: "error",
        detail: t("Greška pri učitavanju klijenata"),
        life: 3000,
      });
    } finally {
      setProcessing(false);
    }
  };

  useEffect(() => {
    refreshPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tableValue = useMemo(() => allPartners, [allPartners]);

  const typeOptions = useMemo(
    () => [
      { label: t("Svi tipovi"), value: null },
      { label: t("Privredno društvo"), value: PartnerType.Company },
      { label: t("Poljoprivredno gazdinstvo"), value: PartnerType.Individual },
    ],
    [t],
  );

  const vatOptions = useMemo(
    () => [
      { label: t("Sve"), value: null },
      { label: t("U PDV-u"), value: true },
      { label: t("Van PDV-a"), value: false },
    ],
    [t],
  );

  const statusOptions = useMemo(
    () => [
      { label: t("Sva stanja"), value: null },
      { label: t("Aktivan"), value: PartnerStatus.Active },
      { label: t("Blokiran"), value: PartnerStatus.Blocked },
    ],
    [t],
  );

  const typeFilterElement = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={typeOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={String(t("Izaberite"))}
        className="p-column-filter"
        showClear={false}
      />
    );
  };

  const vatFilterElement = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={vatOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={String(t("Izaberite"))}
        className="p-column-filter"
        showClear={false}
      />
    );
  };

  const statusFilterElement = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={statusOptions}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={String(t("Izaberite"))}
        className="p-column-filter"
        showClear={false}
      />
    );
  };

  const cityBody = (row: Partner) => {
    return <span>{row?.city?.name ?? ""}</span>;
  };

  const typeBody = (row: Partner) => {
    return (
      <span>
        {row.type === PartnerType.Company
          ? t("Privredno društvo")
          : t("Poljoprivredno gazdinstvo")}
      </span>
    );
  };

  const vatBody = (row: Partner) => {
    return <span>{row.isVat ? t("Da") : t("Ne")}</span>;
  };

  const statusBody = (row: Partner) => {
    const isActive = row.status === PartnerStatus.Active;
    return (
      <div className="action-button">
        <ButtonInput
          addStyle={{
            border: "none",
            background: isActive ? "#198754" : "#DC3545",
          }}
          iconAlign="right"
          icon="bi bi-exclamation-octagon"
          labelText={isActive ? t("Aktivan") : t("Blokiran")}
          onButtonClick={() => {}}
        />
      </div>
    );
  };

  const actionsBody = (row: Partner) => {
    return (
      <div className="action-button">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={async () => {
            try {
              const id = row.id;
              const resp = await getOnePartner(id);
              const partner: Partner = resp?.data?.result ?? resp?.data;
              setSelectedPartner(partner);
              setShowForm(v4());
            } catch (e) {
              // @ts-ignore
              toast.current?.show({
                severity: "error",
                detail: t("Greška pri učitavanju partnera"),
                life: 3000,
              });
            }
          }}
        />
      </div>
    );
  };

  //********* export to excel ******//
  const formatPartnerType = (type?: PartnerType) => {
    if (type === PartnerType.Company) return t("Privredno društvo");
    if (type === PartnerType.Individual) return t("Poljoprivredno gazdinstvo");
    return "";
  };

  const formatPartnerStatus = (status?: PartnerStatus) => {
    if (status === PartnerStatus.Active) return t("Aktivan");
    if (status === PartnerStatus.Blocked) return t("Blokiran");
    return "";
  };

  const formatVat = (isVat?: boolean) => {
    if (isVat === true) return t("Da");
    if (isVat === false) return t("Ne");
    return "";
  };

  const saveAsExcelFile = (buffer: any, fileName: string) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        const EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        const EXCEL_EXTENSION = ".xlsx";

        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });

        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const cleanExcelText = (value?: string | number | null) => {
    if (value === null || value === undefined) return "";
    return String(value).replace(/^'+/, "").trim();
  };

  const exportToExcel = () => {
    const dataForExport =
      filteredPartners && filteredPartners.length > 0
        ? filteredPartners
        : tableValue;

    const sortedPartners = [...dataForExport].sort((a, b) => {
      const codeA = a.code ?? "";
      const codeB = b.code ?? "";
      return codeA.localeCompare(codeB, "sr");
    });

    const excelPartnersObject = sortedPartners.map((item: Partner) => ({
      Id: item.id ?? "",
      SifraKlijenta: item.code ?? "",
      NazivKlijenta: item.name ?? "",
      TipKlijenta: formatPartnerType(item.type),
      Status: formatPartnerStatus(item.status),

      Adresa: item.address ?? "",
      MestoNaselje: item.city?.name ?? "",
      Region: item.region ?? "",

      OdgovornoLiceIme: item.responsiblePersonFirstName ?? "",
      OdgovornoLicePrezime: item.responsiblePersonLastName ?? "",

      BrojLicneKarte: cleanExcelText(item.nationalIdNumber),
      MestoIzdavanjaLicneKarte: item.nationalIdIssueLocation ?? "",
      JMBG: cleanExcelText(item.personalCodeNumber),

      Telefon: cleanExcelText(item.phone),
      MobilniTelefon: cleanExcelText(item.mobile),
      Fax: cleanExcelText(item.fax),
      Email: item.email ?? "",

      UPDVu: formatVat(item.isVat),
      PIB: cleanExcelText(item.vatNumber),
      MaticniBroj: cleanExcelText(item.companyNumber),

      Komercijalista1: item.commercialist1 ?? "",
      Komercijalista2: item.commercialist2 ?? "",

      NazivBanke: item.bankName ?? "",
      SifraBankeJIB: cleanExcelText(item.bankIdNumber),
      BrojRacuna: cleanExcelText(item.bankAccountNumber),
    }));

    const excelObject = [
      {
        Id: "",
        SifraKlijenta: "",
        NazivKlijenta: "",
        TipKlijenta: "",
        Status: "",
        Adresa: "",
        MestoNaselje: "",
        Region: "",
        OdgovornoLiceIme: "",
        OdgovornoLicePrezime: "",
        BrojLicneKarte: "",
        MestoIzdavanjaLicneKarte: "",
        JMBG: "",
        Telefon: "",
        MobilniTelefon: "",
        Fax: "",
        Email: "",
        UPDVu: "",
        PIB: "",
        MaticniBroj: "",
        Komercijalista1: "",
        Komercijalista2: "",
        NazivBanke: "",
        SifraBankeJIB: "",
        BrojRacuna: "",
      },
      ...excelPartnersObject,
    ];

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = {
        Sheets: { Komitenti: worksheet },
        SheetNames: ["Komitenti"],
      };

      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const today = new Date();
      const formattedDate = `${today.getDate().toString().padStart(2, "0")}.${(
        today.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}.${today.getFullYear()}`;

      saveAsExcelFile(excelBuffer, `Komitenti_${formattedDate}`);
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position="top-right" />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-person-lines-fill header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            />
            <span className="header-left-title">{t("Lista klijenata")}</span>
          </div>
          <div className="header-right">
            {(activeUser?.role?.name === RoleType.Superadmin ||
              activeUser?.role?.name === RoleType.Administrator ||
              activeUser?.role?.name === RoleType.Director ||
              activeUser?.role?.name === RoleType.Backoffice) && (
              <ButtonInput
                addStyle={{
                  border: "1px solid #01a453",
                  background: "#ffffff",
                  color: "#01a453",
                  marginRight: "0.5rem",
                }}
                labelText={""}
                icon="bi bi-file-earmark-excel"
                onButtonClick={exportToExcel}
                disabled={processing === true}
              />
            )}
            <NewPartnerForm
              buttonLabel={String(t("Dodaj kupca/dobavljača"))}
              buttonIcon="bi bi-person-lines-fill"
              show={showForm}
              partner={selectedPartner}
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
                margin: "0 1rem 0 0",
              }}
              buttonNewCityStyle={{
                border: "none",
                background: "rgba(21, 115, 71, 1)",
              }}
              buttonSaveStyle={{
                border: "none",
                background: "rgba(0, 165, 83, 1)",
              }}
              buttonInactiveStyle={{
                border: "none",
                background: "rgba(108, 117, 125, 1)",
              }}
              refreshingData={refreshPartners}
            />
          </div>
        </div>

        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              value={tableValue}
              dataKey="id"
              scrollable
              scrollHeight="80vh"
              stripedRows
              showGridlines
              removableSort
              sortMode="single"
              filterDisplay="row"
              filters={filters}
              onFilter={(e) => {
                setFilters(e.filters as DataTableFilterMeta);
                setFirst(0);
              }}
              onValueChange={(filteredData) =>
                setFilteredPartners(filteredData as Partner[])
              }
              paginator
              first={first}
              rows={rows}
              onPage={(e) => {
                setFirst(e.first);
                setRows(e.rows);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate=""
              emptyMessage={t("Nema klijenata za zadate kriterijume")}
            >
              <Column
                field="code"
                header={t("Šifra klijenta")}
                sortable
                filter
                style={{ minWidth: "160px" }}
              />
              <Column
                field="name"
                header={t("Naziv klijenta")}
                sortable
                filter
                style={{ minWidth: "240px" }}
              />
              <Column
                field="address"
                header={t("Adresa")}
                sortable
                filter
                style={{ minWidth: "240px" }}
              />
              <Column
                field="cityName"
                header={t("Mesto/Naselje")}
                body={cityBody}
                sortable
                filter
                style={{ minWidth: "200px" }}
              />
              <Column
                field="mobile"
                header={t("Mobilni telefon")}
                sortable
                filter
                style={{ minWidth: "160px" }}
              />
              <Column
                field="email"
                header={t("Email")}
                sortable
                filter
                style={{ minWidth: "220px" }}
              />
              <Column
                field="type"
                header={t("Tip klijenta")}
                body={typeBody}
                filter
                filterElement={typeFilterElement}
                showFilterMenu={false}
                style={{ minWidth: "220px" }}
              />
              <Column
                field="isVat"
                header={t("PDV")}
                body={vatBody}
                filter
                filterElement={vatFilterElement}
                showFilterMenu={false}
                style={{ minWidth: "140px" }}
              />
              <Column
                field="status"
                header={t("Status")}
                body={statusBody}
                filter
                filterElement={statusFilterElement}
                showFilterMenu={false}
                style={{ minWidth: "170px" }}
              />
              <Column
                header={t("Opcije")}
                body={actionsBody}
                style={{ minWidth: "120px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
